import { LayoutSerVice } from './layout-service';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { Application, Container } from '@tencent/alloy-renderer';
import { IRenderProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';
import { reaction } from 'mobx';

/**
 * 版式manager
 * 维护、缓存所有的版式实例
 * 考虑客户端有母版和版式的修改，先和母版拆开管理
 */
export class LayoutManager {
    private scale: number;
    private layoutCacheMap: { [id: string]: LayoutSerVice | undefined };
    private currentLayoutId: string;
    private getApplication: () => Application;
    private handleStableLayerUpdate: (props: Partial<IRenderProps>) => void;

    public constructor(
        scale = 1,
        getApplication: () => Application,
        handleStableLayerUpdate: (props: Partial<IRenderProps>) => void,
    ) {
        this.scale = scale;
        this.layoutCacheMap = {};
        this.getApplication = getApplication;
        this.handleStableLayerUpdate = handleStableLayerUpdate;

        // 模版被切换后，做一次重建检测，如果缓存的id已经不在数据中了，就删除这个缓存
        reaction(() => [
            this.getAppServices().getUiDataService().presentation.masterList[0]?.uniqueID,
            this.getAppServices().getUiDataService().presentation.masterList.length,
            this.getAppServices().getUiDataService().presentation.layoutList.length,
        ], () => {
            for (const layoutCacheMapKey in this.layoutCacheMap) {
                const index = this.getAppServices().getUiDataService().presentation.layoutList.findIndex((item) => {
                    const layoutId = this.getLayoutId(item);
                    return  layoutId === layoutCacheMapKey;
                });
                if (index === -1) {
                    this.layoutCacheMap[layoutCacheMapKey] = undefined;
                }
            }
        });
    }

    public buildLayoutByCache(layoutModel: ILayoutModel) {
        // 查找当前model的版式是否在缓存中
        let layoutService: LayoutSerVice | undefined = this.getLayoutFromCache(layoutModel);
        if (!layoutService) { // 没缓存过就构建个新的模板缓存
            layoutService = this.buildLayoutService(layoutModel);
        }
        return layoutService;
    }

    /**
     * 查找缓存中是否有该版式
     * @param layoutModel
     */
    public getLayoutFromCache(layoutModel: ILayoutModel): LayoutSerVice | undefined {
        const layoutId = this.getLayoutId(layoutModel);
        return this.layoutCacheMap[layoutId];
    }

    /**
     * 构建一个新的版式并缓存
     * @param layoutModel
     */
    public buildLayoutService(layoutModel: ILayoutModel) {
        const layoutId = this.getLayoutId(layoutModel);

        // 如果拿不到layoutId，则代表当前页不展示，不需要构建
        if (!layoutId) return;

        // 创建版式
        const layoutService = new LayoutSerVice(
            this.scale,
            layoutModel,
            this.getApplication,
            this.handleStableLayerUpdate,
        );

        // 缓存版式
        this.cacheLayout(layoutId, layoutService);

        const { currentSlide } = this.getAppServices().viewableServiceLayer.selectionService;
        // 如果是为当前页构建，则展示版式
        if (currentSlide?.getRelationLayoutModel() === layoutModel && this.isSlideModel(currentSlide)) {
            this.updateLayout(layoutModel);
            layoutService.reloadComponent(layoutModel);
        }
        return layoutService;
    }

    /**
     * 根据slideModel展示版式，并按需更新组件
     */
    public updateLayout(layoutModel: ILayoutModel): void {
        const layoutId = layoutModel ? this.getLayoutId(layoutModel) : '';

        // 更新当前layoutId
        this.currentLayoutId = layoutId;

        // 展示新版式
        if (layoutId && this.isSlideModel(this.getAppServices().viewableServiceLayer.selectionService.currentSlide)) {
            const layoutService = this.layoutCacheMap[layoutId];
            if (layoutService) {
                // 按需刷新版式，由内层判断需不需要重刷
                layoutService.updateWithData(this.scale);
            } else {
                // 防止出现找不到 cache 的情况
                this.buildLayoutByCache(layoutModel);
                this.updateLayout(layoutModel);
            }
        }
    }

    /**
     * 更新manager的宽高数据
     * 更新时，如果宽高变化了，只重刷当前版式，其他版式不刷
     * 切页其他版式时，由版式对比自身与manager的宽高是否不同来决定是否重刷
     * */
    public updateWithData(scale: number): void {
        this.scale = scale;
        this.layoutCacheMap[this.currentLayoutId]?.updateWithData?.(scale);
    }

    /**
     * 获取背景渲染树
     * @param layoutModel
     */
    public getRenderComponent = (layoutModel: ILayoutModel): Container | undefined => {
        let layoutService = this.getLayoutFromCache(layoutModel);
        if (!layoutService) {
            layoutService = this.buildLayoutByCache(layoutModel);
        }
        return layoutService?.getComponent();
    };

    /**
     * 根据slide model获取layoutId
     */
    public getLayoutId(model) {
        const presentationModel = model?.presentationRef;
        if (presentationModel && model?.sdkData) {
            return presentationModel.getLayoutChildrenMapKey(model.sdkData);
        }
    }

    /**
     * 缓存版式canvas元素
     */
    private cacheLayout(layoutId, layoutService) {
        this.layoutCacheMap[layoutId] = layoutService;
    }

    private isSlideModel(model) {
        return model.type === UIModelType.SLIDE;
    }

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }
}

